/**
 * jewelryCalculations.js
 *
 * Single source of truth for all jewelry billing and estimate calculations.
 * Pure JavaScript — no React, no UI dependencies.
 * Used by: Web (React), Mobile (React Native), Tablet.
 *
 * INR screens  → use calculateINRGrandTotal
 * USD screens  → use calculateUSDGrandTotal (adds currency conversion step)
 * Kundan       → see kundanCalculations.js (WIP, separate file)
 */

// ── Gold purity factors (industry standard) ───────────────────────────────────
// 18K = 0.76, 22K = 0.92 per business requirement (not the pure 18/24, 22/24 fractions)
const GOLD_PURITY_FACTORS = {
  24: 0.9999,
  22: 0.92,
  18: 0.76,
  14: 0.5833,
};

/**
 * Returns the purity factor for a given karat value.
 * Accepts numeric (18, 22, 24) or string ('18KT', '22KT', '24KT').
 * Falls back to 18K factor (0.76) if unrecognised.
 *
 * @param {number|string} purity
 * @returns {number}
 */
export const getGoldPurityFactor = (purity) => {
  const karat = parseInt(String(purity).replace(/[^0-9]/g, ''), 10);
  return GOLD_PURITY_FACTORS[karat] ?? GOLD_PURITY_FACTORS[18];
};

// ── Stone weight utilities ────────────────────────────────────────────────────

/**
 * Convert a single stone's weight to grams based on its unit.
 *   carat  → weight × 0.2  (1 ct = 0.2 g)
 *   number/pcs → 0         (count only, no gram deduction)
 *   gram   → direct        (default)
 *
 * @param {number|string} weight
 * @param {string}        unit  - 'carat' | 'nos' | 'pcs' | 'gram' etc.
 * @returns {number} weight in grams
 */
export const stoneWeightToGrams = (weight, unit) => {
  const w = parseFloat(weight) || 0;
  const u = (unit || '').toUpperCase();
  if (u === 'CARAT' || u === 'CARATS' || u === 'CT') return w * 0.2;
  if (u === 'NOS' || u === 'PCS' || u === 'PIECES' || u === 'EACH' || u === 'NUMBER') return 0;
  return w; // gram (default)
};

// ── Core calculations ─────────────────────────────────────────────────────────

/**
 * Purity-adjusted gold rate per gram.
 *   goldRate (24K ₹/gram) × purityFactor
 *
 * @param {number|string} goldRate  - 24K rate in ₹ per gram
 * @param {number|string} purity    - karat value (18, 22, 24)
 * @returns {number}
 */
export const calculatePurityGoldRate = (goldRate, purity) => {
  const rate = parseFloat(goldRate) || 0;
  return rate * getGoldPurityFactor(purity);
};

/**
 * Net weight = gross weight − total stone weight.
 *
 * @param {number|string} grossWeight
 * @param {number|string} stoneWeightTotal - total stone weight already in grams
 * @returns {number}
 */
export const calculateNetWeight = (grossWeight, stoneWeightTotal) => {
  const gross = parseFloat(grossWeight) || 0;
  const stone = parseFloat(stoneWeightTotal) || 0;
  return Math.max(0, gross - stone);
};

/**
 * Inventory Net Weight (gold only) = gross − stone weight − Kundan weight.
 * For Kundan items this excludes the Kundan (matching the vendor's 22 NT WT);
 * Fine Net Weight (gross − stones) keeps it. Non-Kundan items have Kundan 0,
 * so Net Weight and Fine Net Weight are equal.
 *
 * @param {number|string} grossWeight
 * @param {number|string} stoneWeightGrams
 * @param {number|string} kundanWeight
 * @returns {number}
 */
export const calculateGoldNetWeight = (grossWeight, stoneWeightGrams, kundanWeight) =>
  Math.max(0, (parseFloat(grossWeight) || 0) - (parseFloat(stoneWeightGrams) || 0) - (parseFloat(kundanWeight) || 0));

// ── Kundan detection ──────────────────────────────────────────────────────────

/**
 * True when an item code marks a Kundan item (identified by a 'K' prefix,
 * mirroring how small "V" items are detected — see isSmallItemCode below).
 * @param {string} itemCode
 * @returns {boolean}
 */
export const isKundanItemCode = (itemCode) =>
  typeof itemCode === 'string' && itemCode.trim().toUpperCase().startsWith('K');

/** Cost Panel: share taken off a Kundan (K-coded) item's gold cost. */
export const KUNDAN_GOLD_COST_DEDUCTION = 0.06;

/**
 * Cost Panel gold cost after the Kundan deduction — Kundan items' gold cost is
 * reduced by KUNDAN_GOLD_COST_DEDUCTION (6%); other items are unchanged.
 * @param {number|string} goldCost
 * @param {string} itemCode
 * @returns {number}
 */
export const applyKundanGoldCostDeduction = (goldCost, itemCode) => {
  const cost = parseFloat(goldCost) || 0;
  return isKundanItemCode(itemCode) ? Math.ceil(cost * (1 - KUNDAN_GOLD_COST_DEDUCTION)) : cost;
};

/**
 * True when a category (or its parent) is the Kundan category (id 4).
 * Single source for the magic category ID, previously hardcoded separately
 * in each screen's Kundan-field visibility / cost-panel gating.
 * @param {{ id?: number, parent_id?: number }|null} categoryInfo
 * @returns {boolean}
 */
export const isKundanCategory = (categoryInfo) =>
  Boolean(categoryInfo && (categoryInfo.id === 4 || categoryInfo.parent_id === 4));

/**
 * Customer-facing sale net weight = Fine Net Weight = gross − stone weight
 * (grams), the same figure the database keeps in jewelry_pieces.fine_net_weight.
 * No Kundan deduction or 24K conversion — Kundan weight stays in the net as-is.
 * Used for Net Wt and gold/VA/making pricing on every Estimate/Billing screen
 * (web + mobile) and the catalog price; the Cost Panel uses the piece's
 * physical net_weight instead. itemCode / kundanWeight / purity are accepted
 * for call-site compatibility but no longer change the result.
 *
 * @param {Object} args
 * @param {number|string} args.grossWeight
 * @param {number|string} args.stoneWeightTotal
 * @param {string}        args.itemCode
 * @param {number|string} args.kundanWeight
 * @param {number|string} args.purity
 * @returns {number}
 */
export const calculateSaleNetWeight = ({ grossWeight, stoneWeightTotal } = {}) =>
  calculateNetWeight(grossWeight, stoneWeightTotal);

/**
 * Fine weight = net weight × purity factor.
 *
 * @param {number|string} netWeight
 * @param {number|string} purity
 * @returns {number}
 */
export const calculateFineWeight = (netWeight, purity) => {
  return (parseFloat(netWeight) || 0) * getGoldPurityFactor(purity);
};

/**
 * Gold value = net weight × purity gold rate.
 *
 * @param {number|string} goldPrice  - purity-adjusted ₹ per gram
 * @param {number|string} netWeight  - grams
 * @returns {number}
 */
export const calculateGoldValue = (goldPrice, netWeight) => {
  return (parseFloat(goldPrice) || 0) * (parseFloat(netWeight) || 0);
};

/**
 * Wastage amount — always rounds up (consistent across all screens).
 *   wastageGrams = CEIL(netWeight × wastage% / 100,  3 decimal places)
 *   wastageAmt   = CEIL(wastageGrams × goldPrice,     3 decimal places)
 *
 * @param {number|string} netWeight
 * @param {number|string} wastagePercent
 * @param {number|string} goldPrice  - purity-adjusted ₹ per gram
 * @returns {number}
 */
export const calculateWastageAmount = (netWeight, wastagePercent, goldPrice) => {
  const nw = parseFloat(netWeight) || 0;
  const wp = parseFloat(wastagePercent) || 0;
  const gp = parseFloat(goldPrice) || 0;
  const wastageGrams = Math.ceil(nw * (wp / 100) * 1000) / 1000;
  return Math.ceil(wastageGrams * gp * 1000) / 1000;
};

/**
 * Making amount = net weight × making charge per gram.
 *
 * @param {number|string} netWeight
 * @param {number|string} makingCharge  - ₹ per gram
 * @returns {number}
 */
export const calculateMakingAmount = (netWeight, makingCharge) => {
  return (parseFloat(netWeight) || 0) * (parseFloat(makingCharge) || 0);
};

/**
 * Total gold amount = CEIL(goldValue + wastageAmount + makingAmount).
 *
 * @param {number|string} goldValue
 * @param {number|string} wastageAmount
 * @param {number|string} makingAmount
 * @returns {number}
 */
export const calculateTotalGoldAmount = (goldValue, wastageAmount, makingAmount) => {
  const gv = parseFloat(goldValue) || 0;
  const wa = parseFloat(wastageAmount) || 0;
  const ma = parseFloat(makingAmount) || 0;
  return Math.ceil(gv + wa + ma);
};

// ── Stone total ───────────────────────────────────────────────────────────────

/**
 * Group stones by material code into per-category totals, for the PDF/estimate
 * "stone category breakdown" and for live weight/cost totals while stones are
 * being edited. Derives fresh from the current stones array every time it's
 * called rather than being cached in state, so it can never go stale after a
 * stone is edited or the item is re-fetched.
 *
 * Field names on each stone are looked up permissively (`stone_code` or
 * `code`, `sale_price` or `rate`, `stone_name` or `name`) so this works with
 * both the raw shape returned by the jewelry-details API (billing screens'
 * send-time payload) and the normalized shape each screen's Stone Section
 * keeps in local state while staff edit rates — previously duplicated
 * verbatim in IndianBilling.js/USBilling.js (raw shape) and re-implemented
 * separately, with USDEstimate.js's version live and INREstimate.js's
 * version missing entirely (only set once at initial fetch, going stale on
 * edits), in the two Estimate screens' Stone Section (normalized shape).
 *
 * @param {Array<{ stone_code?: string, code?: string, stone_name?: string, name?: string, unit?: string, weight, sale_price?: number, rate?: number }>} stonesArr
 * @returns {Object<string, { category: string, unit: string, weight: number, rawWeight: number, cost: number, count: number }>}
 */
export const buildStoneCategoryTotals = (stonesArr) => {
  if (!Array.isArray(stonesArr) || stonesArr.length === 0) return {};
  const totals = {};
  stonesArr.forEach((s) => {
    const code = s.stone_code || s.code || s.stone_name || s.name || 'Unknown';
    const unit = (s.unit || 'CARAT').toUpperCase();
    const rawWeight = parseFloat(s.weight) || 0;
    const rate = parseFloat(s.sale_price ?? s.rate) || 0;
    const cost = rawWeight * rate;
    const weightGrams = stoneWeightToGrams(rawWeight, unit);
    if (!totals[code]) {
      // materialCategory = materials.category (Diamond / Stone / Beads …) — bill PDFs split on it
      totals[code] = { category: s.stone_name || s.name || code, materialCategory: s.category ?? null, unit, weight: 0, rawWeight: 0, cost: 0, count: 0 };
    }
    totals[code].weight += weightGrams;
    totals[code].rawWeight += rawWeight;
    totals[code].cost += cost;
    totals[code].count += 1;
  });
  return totals;
};

/**
 * Calculate stone totals from an array of stone objects.
 *
 * @param {Array<{ weight, unit, rate, purchaseRate }>} stones
 * @returns {{ saleTotal: number, purchaseTotal: number, totalWeightGrams: number }}
 */
export const calculateStoneTotal = (stones = []) => {
  let saleTotal = 0;
  let purchaseTotal = 0;
  let totalWeightGrams = 0;

  stones.forEach((stone) => {
    const weightGrams = stoneWeightToGrams(stone.weight, stone.unit);
    const rate        = parseFloat(stone.rate)         || 0;
    const purchaseRate = parseFloat(stone.purchaseRate) || 0;

    saleTotal        += weightGrams * rate;
    purchaseTotal    += weightGrams * purchaseRate;
    totalWeightGrams += weightGrams;
  });

  return { saleTotal, purchaseTotal, totalWeightGrams };
};

// ── Certification charges ─────────────────────────────────────────────────────

/**
 * Certification charges = CEIL(diamondCarats × chargePerCarat).
 * Returns 0 if certification is not required or carats = 0.
 *
 * @param {number|string} diamondCarats
 * @param {number|string} chargePerCarat
 * @param {boolean}       certificationRequired
 * @returns {number}
 */
export const calculateCertificationCharges = (diamondCarats, chargePerCarat, certificationRequired) => {
  if (!certificationRequired) return 0;
  const carats = parseFloat(diamondCarats) || 0;
  const charge = parseFloat(chargePerCarat) || 0;
  if (carats <= 0) return 0;
  return Math.ceil(carats * charge);
};

// ── Jewel value (pre-tax) ─────────────────────────────────────────────────────

/**
 * Jewel value = CEIL(totalGoldAmount) + CEIL(stoneTotal) + certCharges.
 *
 * @param {number|string} totalGoldAmount
 * @param {number|string} stoneTotal
 * @param {number}        certCharges
 * @returns {number}
 */
export const calculateJewelValue = (totalGoldAmount, stoneTotal, certCharges = 0) => {
  return Math.ceil(parseFloat(totalGoldAmount) || 0)
       + Math.ceil(parseFloat(stoneTotal) || 0)
       + (parseFloat(certCharges) || 0);
};

// ── INR grand total (IndianBilling + INREstimate) ─────────────────────────────

/**
 * INR grand total with GST.
 *   gstAmount  = CEIL(jewelValue × gstPercent / 100)
 *   grandTotal = jewelValue + gstAmount
 *
 * @param {number} jewelValue
 * @param {number|string} gstPercent
 * @returns {{ gstAmount: number, grandTotal: number }}
 */
export const calculateINRGrandTotal = (jewelValue, gstPercent) => {
  const gst       = parseFloat(gstPercent) || 0;
  const gstAmount = Math.ceil(jewelValue * gst / 100);
  return { gstAmount, grandTotal: jewelValue + gstAmount };
};

// ── USD grand total (USBilling + USDEstimate) ─────────────────────────────────

/**
 * USD grand total with tax and currency conversion.
 *   jewelValueUSD  = jewelValueINR / dollarRate
 *   taxAmountUSD   = CEIL(jewelValueUSD × taxPercent / 100, 2 decimals)
 *   taxAmountINR   = CEIL(taxAmountUSD × dollarRate)
 *   grandTotalINR  = jewelValueINR + taxAmountINR
 *   grandTotalUSD  = jewelValueUSD + taxAmountUSD
 *
 * @param {number} jewelValueINR
 * @param {number|string} taxPercent   - combined tax % (customs + state)
 * @param {number|string} dollarRate   - effective INR per 1 USD
 * @returns {{ taxAmountUSD: number, taxAmountINR: number, grandTotalINR: number, grandTotalUSD: number }}
 */
export const calculateUSDGrandTotal = (jewelValueINR, taxPercent, dollarRate) => {
  const rate    = parseFloat(dollarRate) || 1;
  const taxPct  = parseFloat(taxPercent) || 0;

  const jewelValueUSD = jewelValueINR / rate;
  const taxAmountUSD  = Math.ceil(jewelValueUSD * taxPct / 100 * 100) / 100;
  const taxAmountINR  = Math.ceil(taxAmountUSD * rate);
  const grandTotalINR = jewelValueINR + taxAmountINR;
  const grandTotalUSD = jewelValueUSD + taxAmountUSD;

  return { taxAmountUSD, taxAmountINR, grandTotalINR, grandTotalUSD };
};

// ── Cost Panel (internal acquisition-cost / profit tool) ──────────────────────
// Used by every Estimate and Billing screen's hidden "Hold ` to view" panel.
// Each screen still computes its own Gold Cost (Kundan fine-weight vs simple
// purity-adjusted rate) and Tax (flat customs %, GST %, or shipment-cost
// override) — those differ by business rules and stay screen-specific. This
// centralizes the one formula that must never drift between screens again.

/**
 * Pre-tax acquisition cost = what the business actually paid the vendor.
 * Wastage and making are deliberately excluded — vendors don't charge either
 * on purchase, so including them overstates cost and understates profit.
 *
 * @param {Object} costs
 * @param {number|string} costs.goldCost
 * @param {number|string} costs.stoneCost  - includes diamond cost, there is no separate diamond-cost field
 * @param {number|string} costs.laborCost
 * @param {number|string} costs.certCost
 * @param {number|string} costs.otherCost  - jewelry_pieces.other_costs (e.g. hallmarking)
 * @returns {number}
 */
export const calculateAcquisitionCost = ({ goldCost = 0, stoneCost = 0, laborCost = 0, certCost = 0, otherCost = 0 } = {}) => {
  return (parseFloat(goldCost) || 0)
       + (parseFloat(stoneCost) || 0)
       + (parseFloat(laborCost) || 0)
       + (parseFloat(certCost) || 0)
       + (parseFloat(otherCost) || 0);
};

/**
 * Acquisition cost with tax applied — the pre-tax-cost-plus-tax arithmetic
 * that was hand-rolled separately in every screen's Cost Panel. Screens still
 * decide their own taxPercent (live GST%, flat customs %, or a shipment-cost
 * override) — that selection is a real business-rule difference and stays
 * screen-specific; this just unifies the formula once that number is known.
 *
 * @param {Object} args
 * @param {number|string} args.goldCost
 * @param {number|string} args.stoneCost
 * @param {number|string} args.laborCost
 * @param {number|string} args.certCost
 * @param {number|string} args.otherCost  - taxed like labour (included before tax)
 * @param {number|string} args.taxPercent
 * @returns {{ preTaxCost: number, taxAmount: number, totalCost: number }}
 */
export const calculateAcquisitionCostWithTax = ({ goldCost, stoneCost, laborCost, certCost, otherCost, taxPercent } = {}) => {
  const preTaxCost = calculateAcquisitionCost({ goldCost, stoneCost, laborCost, certCost, otherCost });
  const taxAmount = Math.ceil((preTaxCost * (parseFloat(taxPercent) || 0)) / 100);
  return { preTaxCost, taxAmount, totalCost: preTaxCost + taxAmount };
};

// ── Kundan fine weight (internal cost basis) ──────────────────────────────────
// Used only by the hidden staff Cost Panel to determine the business's true
// gold cost for Kundan items — separate from calculateSaleNetWeight above,
// which adjusts the customer-facing quoted net weight. Moved here from the
// previously-unused kundanCalculations.js so there's one shared, imported
// implementation instead of ad-hoc copies (some missing the kundanWeight
// term entirely) scattered across the 4 screens' Cost Panels.

/**
 * Total fine gold weight for a Kundan item's true cost basis.
 *   = (netWeight × finePercent / 100) + (kundanWeight × kundanPercent / 100)
 *
 * @param {number|string} netWeight     - net gold weight, excluding Kundan weight (grams)
 * @param {number|string} finePercent   - 22 FINE % from DB (e.g. 104)
 * @param {number|string} kundanWeight  - KUN WT from DB (grams)
 * @param {number|string} kundanPercent - K FINE % from DB (e.g. 125)
 * @returns {number}
 */
export const calculateKundanFineWeight = (netWeight, finePercent, kundanWeight, kundanPercent) => {
  const nw = parseFloat(netWeight) || 0;
  const fp = parseFloat(finePercent) || 0;
  const kw = parseFloat(kundanWeight) || 0;
  const kp = parseFloat(kundanPercent) || 0;
  return parseFloat(((nw * fp / 100) + (kw * kp / 100)).toFixed(3));
};

/**
 * Cost Panel bottom line: actual cost, profit/loss vs. the quoted price, and
 * profit as a percentage of actual cost — always in INR. Pass dollarRate to
 * also get the USD-converted figures (US screens show ₹ / $ side by side),
 * so every screen shares one conversion instead of each keeping its own
 * local toUSD() helper.
 *
 * @param {Object} args
 * @param {number|string} args.preTaxCost  - from calculateAcquisitionCost
 * @param {number|string} args.taxAmount   - screen-specific (customs/GST/shipment)
 * @param {number|string} args.quotedCost  - what the customer is being charged, in INR
 * @param {number|string} [args.dollarRate] - INR per 1 USD; omit for INR-only screens
 * @returns {{ actualCost: number, profit: number, profitPct: string,
 *             actualCostUSD?: number, quotedCostUSD?: number, profitUSD?: number }}
 */
export const calculateCostPanelResult = ({ preTaxCost, taxAmount, quotedCost, dollarRate } = {}) => {
  const actualCost = (parseFloat(preTaxCost) || 0) + (parseFloat(taxAmount) || 0);
  const quoted = parseFloat(quotedCost) || 0;
  const profit = quoted - Math.ceil(actualCost);
  const profitPct = actualCost > 0 ? ((profit / Math.ceil(actualCost)) * 100).toFixed(1) : '0.0';

  const result = { actualCost, profit, profitPct };
  const rate = parseFloat(dollarRate) || 0;
  if (rate > 0) {
    result.actualCostUSD = actualCost / rate;
    result.quotedCostUSD = quoted / rate;
    result.profitUSD = profit / rate;
  }
  return result;
};

// ── Small Items ("V" category) ────────────────────────────────────────────────
// Small pieces (small chains, small lockets, etc.) are not priced by the normal
// gold + wastage + making + stone breakdown. Instead the customer sees ONE clean
// price = actual cost + 30%, rounded UP to a clean step (nearest $100 on USD,
// nearest ₹100 on INR). The cost itself is unchanged — only the final price is
// derived from it. Identified purely by item-code prefix, mirroring how Kundan
// items are detected by a 'K' prefix.

export const SMALL_ITEM_PREFIX = 'V';

/**
 * True when an item code marks a "small item" (V-category).
 * @param {string} code - item/piece code, e.g. 'V-12'
 * @returns {boolean}
 */
export const isSmallItemCode = (code) =>
  typeof code === 'string' && code.trim().toUpperCase().startsWith(SMALL_ITEM_PREFIX);

/**
 * Small-item selling price = actual cost + 30%, rounded UP to `step`.
 * Because it rounds up, realized markup is always ≥ 30%.
 * Works in whatever currency `cost` is passed in (convert to USD first for the
 * USD estimate so the $100 step is applied in dollars).
 *
 * @param {number|string} cost - actual cost in the target currency
 * @param {number}        [step=100] - rounding step (100 = nearest hundred)
 * @param {number}        [markup=1.30] - 1.30 = add 30%
 * @returns {number} rounded selling price (0 when cost is missing/≤0)
 */
export const calculateSmallItemPrice = (cost, step = 100, markup = 1.30) => {
  const c = parseFloat(cost) || 0;
  const s = parseFloat(step) || 100;
  if (c <= 0) return 0;
  return Math.ceil((c * markup) / s) * s;
};
